import _ from "lodash";
import { useEffect, useMemo, useState } from "react";

import useService from "../../../../hooks/useService";
import { getModalHandler } from "../../Common/Modals/hooks/useModalSingleton";
import {
  buildContractCreateWithPaymentUploadPayload,
  buildPatchPayload,
  mapContractToFormState,
} from "../utils/contractFormUtils";

export default function useContractsEditModal(props) {
  const {
    contractData,
    entityId,
    mode = "edit",
    openModal,
    handleCloseModal,
    onSaveSuccess,
  } = props;
  const contractsRepository = useService("repositories/contracts-repository");
  const { openSuccessModal, openFailureModal } = getModalHandler();
  const initialFormValues = useMemo(() => mapContractToFormState(contractData), [contractData]);
  const [formValues, setFormValues] = useState(initialFormValues);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (openModal) {
      setFormValues(initialFormValues);
      setIsSaving(false);
    }
  }, [initialFormValues, openModal]);

  const initialPayload = useMemo(() => buildPatchPayload(initialFormValues), [initialFormValues]);
  const currentPayload = useMemo(() => buildPatchPayload(formValues), [formValues]);
  const initialPaymentFields = useMemo(
    () =>
      _.pick(initialFormValues, [
        "reviewContract",
        "clientManagementSplit",
        "providerSplit",
        "marketingSplit",
      ]),
    [initialFormValues]
  );
  const currentPaymentFields = useMemo(
    () =>
      _.pick(formValues, [
        "reviewContract",
        "clientManagementSplit",
        "providerSplit",
        "marketingSplit",
      ]),
    [formValues]
  );
  const isFileDirty = !_.isEqual(initialFormValues.contractFile, formValues.contractFile);
  const isPaymentFieldsDirty = !_.isEqual(initialPaymentFields, currentPaymentFields);
  const isDirty =
    !_.isEqual(initialPayload, currentPayload) || isFileDirty || (mode === "add" && isPaymentFieldsDirty);

  const handleFieldOnChange = (fieldName, value) => {
    setFormValues((prev) => ({ ...prev, [fieldName]: value }));
  };

  const handleCancel = () => setFormValues(initialFormValues);

  const handleSave = async () => {
    if (!isDirty) {
      return;
    }

    try {
      setIsSaving(true);
      if (mode === "add") {
        await contractsRepository.createWithPayment(
          buildContractCreateWithPaymentUploadPayload(formValues, entityId)
        );
      } else if (contractData?.id) {
        if (Object.keys(currentPayload).length > 0) {
          await contractsRepository.patch(contractData.id, currentPayload);
        }

        if (formValues.contractFile instanceof File) {
          await contractsRepository.upload(
            { contract_file: formValues.contractFile },
            { urlPath: `contracts/${contractData.id}/` },
            "PATCH"
          );
        }
      } else {
        return;
      }
      await onSaveSuccess?.();
      openSuccessModal();
      handleCloseModal();
    } catch (error) {
      openFailureModal(error);
    } finally {
      setIsSaving(false);
    }
  };

  return { formValues, isDirty, isSaving, handleFieldOnChange, handleCancel, handleSave };
}
